import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { LoadingSpinner } from '@/components/ui/loading-spinner';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { ACCENT_PROJECT } from '../../lib/personAccents';

interface PersonHeroProps {
  name: string;
  department: string;
  faculty: string;
  resultsCount: number;
  practicalResultsCount: number;
  projectsCount: number;
  competenciesCount: number;
}

export default function PersonHero({
  name,
  department,
  faculty,
  resultsCount,
  practicalResultsCount,
  projectsCount,
  competenciesCount,
}: PersonHeroProps) {
  const { t } = useTranslation('person');

  const affiliation = department === faculty ? department : `${department} (${faculty})`;

  type Tone = 'default' | 'practical' | 'project';
  const stats: { label: string; value: number; tone: Tone; tooltip: string | null }[] = [
    {
      label: t('stats.results'),
      value: resultsCount,
      tone: 'default',
      tooltip: t('stats.resultsTooltip'),
    },
    {
      label: t('stats.practicalResults'),
      value: practicalResultsCount,
      tone: 'practical',
      tooltip: t('stats.practicalResultsTooltip'),
    },
    {
      label: t('stats.projects'),
      value: projectsCount,
      tone: 'project',
      tooltip: null,
    },
    {
      label: t('stats.competencies'),
      value: competenciesCount,
      tone: 'default',
      tooltip: t('stats.competenciesTooltip'),
    },
  ];

  return (
    <Card className="mb-6 border-ctu-border shadow-none">
      <CardContent className="p-8">
        <div className="min-w-0">
          <h1 className="text-3xl font-semibold tracking-tight text-ctu-text mb-2">{name}</h1>
          <p className="text-base text-ctu-text-muted m-0">{affiliation}</p>
        </div>

        <Separator className="my-6" />
        <TooltipProvider delayDuration={150}>
          <dl className="grid grid-cols-2 gap-x-8 gap-y-4 sm:grid-cols-4">
            {stats.map(({ label, value, tone, tooltip }) => {
              const labelClass =
                'text-[11px] font-semibold uppercase tracking-[0.12em] text-ctu-text-muted';
              const baseValueClass = 'text-2xl font-semibold tabular-nums';
              const valueClass =
                tone === 'practical'
                  ? `${baseValueClass} text-primary`
                  : tone === 'project'
                    ? baseValueClass
                    : `${baseValueClass} text-ctu-text`;
              const valueStyle =
                tone === 'project' ? { color: ACCENT_PROJECT } : undefined;
              const labelNode = tooltip ? (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <dt
                      tabIndex={0}
                      className={`${labelClass} cursor-help underline decoration-dotted decoration-ctu-text-muted/40 underline-offset-4`}
                    >
                      {label}
                    </dt>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" align="start" className="max-w-xs">
                    {tooltip}
                  </TooltipContent>
                </Tooltip>
              ) : (
                <dt className={labelClass}>{label}</dt>
              );

              return (
                <div key={label} className="flex flex-col gap-1">
                  {labelNode}
                  <dd className={valueClass} style={valueStyle}>
                    {value}
                  </dd>
                </div>
              );
            })}
          </dl>
        </TooltipProvider>
      </CardContent>
    </Card>
  );
}

export function PersonHeroSkeleton() {
  const { t } = useTranslation('person');
  return <LoadingSpinner className="py-16" text={t('loadingDetails')} />;
}
